import type * as React from 'react';
import { getIntrinsicElementProps, resolvePositioningShorthand, slot } from '@fluentui/react-components';
import type { SensitivityTooltipProps, SensitivityTooltipState } from './SensitivityTooltip.types';
import { PreviewSurface } from '@fluentai/react-preview';

// If you add JSX to this file, be sure to change the file type to .tsx

/**
 * Create the state required to render SensitivityTooltip.
 *
 * The returned state can be modified with hooks such as useSensitivityTooltipStyles_unstable,
 * before being passed to renderSensitivityTooltip_unstable.
 *
 * @param props - props from this instance of SensitivityTooltip
 * @param ref - reference to root HTMLElement of SensitivityTooltip
 */
export const useSensitivityTooltip_unstable = (
  props: SensitivityTooltipProps,
  ref: React.Ref<HTMLElement>,
): SensitivityTooltipState => {
  return {
    positioning: { offset: 8, ...resolvePositioningShorthand(props.positioning) },
    components: {
      root: 'span',
      heading: 'span',
      message: 'span',
      previewSurface: PreviewSurface,
      trigger: 'span',
    },
    root: slot.always(
      getIntrinsicElementProps('span', {
        ref,
        ...props,
      }),
      { elementType: 'span' },
    ),
    heading: slot.optional(props.heading, { elementType: 'span' }),
    message: slot.optional(props.message, { elementType: 'span' }),
    previewSurface: slot.optional(props.previewSurface, {
      defaultProps: {
        tabIndex: 0,
      },
      elementType: PreviewSurface,
      renderByDefault: true,
    }),
    trigger: slot.optional(props.trigger, {
      elementType: 'span',
      defaultProps: { children: props.children },
      renderByDefault: true,
    }),
  };
};
